import React, {createContext, useState} from "react";
import {RootState} from "../../../reducers";
import {connect, DispatchProp} from "react-redux";
import {getCart} from "../Cart/reducer";
import {useLocation, useNavigate} from "react-router-dom";
import {usePrefix} from "../Prefix";
import {useCart} from "../Cart/cartService";
import VoucherUtils from "../User/VoucherUtils";
import {getVouchers} from "../User/reducer";
import {api} from "../Api/api";
import {getCategories, getItemGroups, getItems, getMenus} from "../Menu/reducer";
import AppConfig from "../AppConfig";
import PromotionItemAddModal from "../../components/Modal/PromotionItemAddModal";
import ItemAvailabilityHelper from "../Menu/services/Item/ItemAvailabilityHelper";
import {getSession} from "../Session/reducer";
import {PromotionApi} from "../Api/types";

interface VoucherProductsContextApi {
    promotion: any;
    setPromotion: (promotion: any) => void;
    showVoucherProductsModal: boolean;
    setShowVoucherProductsModal: (show: boolean) => void;
    handleOnPromotionLimit: (newItem: any) => void;
    promotionItems: any[];
    handlePromotionChange: (promotionId?: string) => Promise<void>;
    handleRemovePromotion: () => Promise<void>;
}

export const VoucherProductsContext = createContext<VoucherProductsContextApi>({
    promotion: undefined,
    setPromotion: (promotion: any) => {
    },
    showVoucherProductsModal: false,
    setShowVoucherProductsModal: () => {
    },
    handleOnPromotionLimit: (newItem: any) => {
    },
    promotionItems: [],
    handleRemovePromotion: async () => {
    },
    handlePromotionChange: async (promotionId?: string) => {
    }
});

interface OwnProps {
    children: React.ReactNode;
    isMultilevelMenu: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;
const VoucherProductsProvider = ({
                                     children,
                                     userVouchers,
                                     cart,
                                     menus,
                                     menuItemGroups,
                                     menuItems,
                                     isMultilevelMenu,
                                     session,
                                     categories
                                 }: Props) => {
    const [promotion, setPromotion] = useState<PromotionApi | undefined>();
    const [showVoucherProductsModal, setShowVoucherProductsModal] = useState(false);
    const navigate = useNavigate();
    const prefix = usePrefix();
    const location = useLocation();
    const [promotionItems, setPromotionItems] = useState<any[]>([]);
    const [promotionItemsInCart, setPromotionItemsInCart] = useState<any[]>([]);
    const cartService = useCart();
    const vouchers = VoucherUtils.getVoucherWithCarts(userVouchers, cart);
    const [promotionItem, setPromotionItem] = useState<Record<string, any> | undefined>();
    const locationPathNamesUsingPromotionModal = ["cart"];

    const fetchPromotion = async (promotionId?: string) => {
        if (!promotionId) return;

        try {
            const res = await api.getPromotion(promotionId);
            setPromotion(res);
            return res;
        } catch (error) {
        }
    }

    const findPromotionItemsInMenu = (promotionItems: any[]) => {
        const promotionItemsIds = promotionItems.map(item => item.entity_id);

        return menuItems.filter((menuItem: any) => {
            return promotionItemsIds.includes(menuItem.id);
        })
    }

    const findMenuForPromotionItem = (promotionItem: any) => {
        const itemCategory = ItemAvailabilityHelper.getCategoryWithNearestAvailableDateForItem(promotionItem, categories, menus, session, undefined);

        if (!itemCategory) return undefined;

        return menus.find((menu: Record<string, any>) => menu.categories.find((menuCategory: Record<string, any>) => menuCategory.category_id === itemCategory.id))?.id;
    }

    const handlePromotionChange = async (promotionId?: string) => {
        setPromotionItemsInCart([]);
        const promotion = await fetchPromotion(promotionId);

        if (!promotion) return;

        const promotionItems = findPromotionItemsInMenu(promotion.entities.filter((entity: any) => entity.type === "ITEM"));
        setPromotionItems(promotionItems);

        if (promotionItems.length === 1) {
            const locationPathnameParts = location.pathname.split("/");
            if (locationPathnameParts.some(part => locationPathNamesUsingPromotionModal.includes(part))) {
                setPromotionItem(promotionItems[0]);
            } else {
                const menuId = findMenuForPromotionItem(promotionItems[0]);

                if (isMultilevelMenu && !menuId) return;

                const link = isMultilevelMenu ? `/menu/${menuId}/add/${promotionItems[0].id}` : `/menu/add/${promotionItems[0].id}`;
                navigate(AppConfig.getLinkWithPrefix(link, prefix), {
                    state: {
                        back: true,
                        promotionId: promotion.id
                    }
                });
            }
        } else if (promotionItems.length > 1) {
            setShowVoucherProductsModal(true);
        }
    }

    const handleRemovePromotion = async () => {
        setPromotion(undefined);
        if (cart.coupon) {
            await cartService.updateOrderCoupon({
                code: ""
            });
        }

        const finalPromotionId = promotion?.id;
        const promotionToRemove = cart.promotions.find((cartPromotion: any) => cartPromotion.promotion_id === finalPromotionId);

        if (promotionToRemove) {
            await cartService.removePromotion(promotionToRemove.id);
        } else {
            const voucherToRemove = cart.promotions.find((cartPromotion: any) => vouchers.find(voucher => voucher.voucher?.id === cartPromotion.reference_id));
            if (voucherToRemove) {
                await cartService.removePromotion(voucherToRemove.id);
            }
        }
    }

    const checkIfPromotionHasItemsLimit = (newItem: any, internalPromotion?: PromotionApi) => {
        const finalPromotion = internalPromotion ?? promotion;
        if (finalPromotion) {
            const newPromotionItemsInCart = [...promotionItemsInCart, newItem]
            setPromotionItemsInCart(newPromotionItemsInCart)

            const usageLimit = finalPromotion.new_version ? finalPromotion.usage_limit : finalPromotion.limit_per_order;
            return usageLimit !== undefined && newPromotionItemsInCart.length >= usageLimit;
        }
    }

    const handleOnPromotionLimit = (newItem: any) => {
        if (checkIfPromotionHasItemsLimit(newItem)) {
            setPromotionItemsInCart([]);
            setShowVoucherProductsModal(false);
        }
    }

    const handleOnPromotionItemModalHide = () => {
        setPromotionItem(undefined);
    }

    const handlePromotionItemModalShowChange = (value: boolean) => {
        if (!value) {
            setPromotionItem(undefined);
        }
    };

    return <VoucherProductsContext.Provider value={{
        promotion,
        setPromotion,
        showVoucherProductsModal,
        setShowVoucherProductsModal,
        handleOnPromotionLimit,
        promotionItems,
        handleRemovePromotion,
        handlePromotionChange
    }}>
        <>
            {promotionItem && <PromotionItemAddModal
                items={menuItems}
                show={!!promotionItem}
                vouchers={[]}
                itemGroups={menuItemGroups}
                itemId={promotionItem.id}
                cart={cart}
                menus={menus}
                onHide={handleOnPromotionItemModalHide}
                setShow={handlePromotionItemModalShowChange}
            />}
            {children}
        </>
    </VoucherProductsContext.Provider>;
}

const mapStateToProps = (state: RootState) => ({
    cart: getCart(state.cart),
    userVouchers: getVouchers(state.user),
    menus: getMenus(state.menu),
    menuItems: getItems(state.menu),
    menuItemGroups: getItemGroups(state.menu),
    categories: getCategories(state.menu),
    session: getSession(state.session)
})
const connector = connect(mapStateToProps);


export default connector(VoucherProductsProvider);